import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@material-ui/core';

import { openModal } from 'features/utilsSlice';
import { getRequest } from 'utils/api';
import useIsBuyer from 'hooks/useIsBuyer';
import useNotLoggedInRedirect from 'hooks/useNotLoggedInRedirect';
import useConfirmationRedirect from 'hooks/useConfirmationRedirect';
import useCurrentUser from 'hooks/useCurrentUser';
import useProfileOwner from 'hooks/useProfileOwner';
import useScrollToTop from 'hooks/useScrollToTop';
import useCurrentProfile from 'hooks/useCurrentProfile';
import DocumentHead from 'components/DocumentHead';
import PublicGigDialog from 'components/gigs/PublicGigDialog';
import DiscoverGigs from 'components/gigs/DiscoverGigs';
import DiscoverInfluencers from 'components/gigs/DiscoverInfluencers';
import PurpleCard from 'components/PurpleCard';
import EditAdvertiserProfile from 'components/advertiserProfile/EditAdvertiserProfile';
import Header from 'components/agency/Header';
import MiniAlert from 'components/profileVerification/MiniAlert';
import ExternalLink from 'components/ExternalLink';
import styles from 'styles/components/Discover.module.scss';

const linkStyle = { display: 'flex', marginTop: 5, fontSize: 14, color: '#5b00db' };

const Discover = () => {
  const [influencerNetworks, setInfluencerNetworks] = useState([]);
  const isBuyer = useIsBuyer();
  const isDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const profile = useCurrentProfile();
  const profileOwner = useProfileOwner(profile);

  const dispatch = useDispatch();

  const filteredInfluencerNetworks = influencerNetworks.filter(
    influencerNetwork => influencerNetwork.network.automatic_verification
  );

  const verifiedAll = filteredInfluencerNetworks.every(influencerNetwork =>
    ['verified', 'pending_verification'].includes(influencerNetwork.status)
  );

  const getTitle = () => (isBuyer ? 'Discover Influencers' : 'Discover Offers');

  const getDescription = () =>
    isBuyer ? (
      <div>
        Here you can browse creators and buy promo directly from them.
        <br />
        <ExternalLink
          preview="url"
          mobileUrl="https://gigsocial.crisp.help/en-us/article/tips-for-buying-promo-effectively-1na36wn/?bust=1741115739628"
          desktopUrl="https://gigsocial.crisp.help/en-us/article/tips-for-buying-promo-effectively-1na36wn/?bust=1741115739628"
          target="_blank"
          rel="noreferrer noopener"
          page="Promo Tips"
          linkStyle={linkStyle}
        >
          Tips for Buying Promo Effectively
        </ExternalLink>
      </div>
    ) : (
      'Find and request offers from paying advertisers. When you request an offer, the request will be for the amount you charge for that item.'
    );

  useEffect(() => {
    if (!profile?.id) return;

    getRequest({
      endpoint: 'influencer_networks',
      query: { profile_id: profile.id },
    }).then(networks => setInfluencerNetworks(networks));
  }, [profile?.id]);

  const unverifiedNetworkDescription = (
    <>
      You have not verified all of your social platforms and will not be able to see all the gigs.
      Please return to your{' '}
      <span className={styles.link}>
        <Link href={`u/${profile.username}`}>profile page</Link>
      </span>{' '}
      to finish your verification.
    </>
  );

  const unsettledTraitsDescription = (
    <>
      <span
        onClick={() => dispatch(openModal({ name: 'Inventory Edit', data: { step: 'Traits' } }))}
        className="hover"
        style={{ color: '#5b00db', fontFamily: 'Avenir Heavy' }}
      >
        Add your traits{' '}
      </span>
      for better matching gig results.
    </>
  );

  const unsettledTraits = profile.adult && (profile.trait_ids?.length === 0 || !profile.trait_ids);

  return (
    <>
      {profileOwner && !isBuyer && unsettledTraits && (
        <MiniAlert description={unsettledTraitsDescription} />
      )}
      {profileOwner && !verifiedAll && !isBuyer && (
        <MiniAlert description={unverifiedNetworkDescription} />
      )}

      <Header title={`${isDesktop ? 'Discover' : getTitle()}`} columnDirection>
        <div className={styles.description}>{getDescription()}</div>
      </Header>

      {isBuyer ? <DiscoverInfluencers /> : <DiscoverGigs />}
    </>
  );
};

const AgencyDiscover = () => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const showCard = currentUser?.confirmed && !currentUser.avatar_url;

  return showCard ? (
    <div style={{ padding: 15 }}>
      <PurpleCard
        title="Get full access"
        description={'Complete your profile to get access to our influencer discover.'}
        buttonText={'Complete profile'}
        handleClick={() => {
          dispatch(openModal({ name: 'Edit Advertiser Profile' }));
        }}
      />

      <EditAdvertiserProfile />
    </div>
  ) : (
    <DiscoverInfluencers />
  );
};

const DiscoverPage = () => {
  const currentUser = useSelector(state => state.auth.currentUser);
  const isLoggedIn = useSelector(state => state.auth.loggedIn);

  useNotLoggedInRedirect();
  useConfirmationRedirect('/access-confirmation');
  useScrollToTop();

  if (!isLoggedIn) return null;

  return (
    <div className={styles.discoverContainer}>
      <DocumentHead>
        <title>Discover | GigSocial</title>
      </DocumentHead>

      {currentUser.agency ? <AgencyDiscover /> : <Discover />}

      <PublicGigDialog />
    </div>
  );
};

export default DiscoverPage;
